import { takeLatest, channel } from "redux-saga";
import { put, take, call, fork, select } from "redux-saga/effects";

import { INITIALIZE_PLANS, REQUEST_PLANS, requestPlans, setIsLoadingPlans, receivePlans, receivePricing } from "./Actions";
import { fetchPlans, fetchPricing } from "./api";
import { PAGE_SIZE, THREADS } from "./Constants";
import { getPlans } from "./Selectors";
import { setPricesLoading } from "./Utils";

/*
  The PlanLoader feature has 2 responsibilities:
  1. Load all available plans.
     The app using PlanLoader is responsible for dispatching
     the INITIALIZE_PLANS action which will start the
     loading of plans into the store.
  2. Request new pricing for plans.
     The app using the PlanLoader is responsible for
     knowing when prices need to be cleared and refetched
*/

/*
  Plan Loading functionality
*/
export function* watchInitializePlans() {
  const { payload } = yield take(INITIALIZE_PLANS);
  yield put(requestPlans(payload.companyIDToken));
}
export function* watchRequestPlans() {
  yield takeLatest(REQUEST_PLANS, function* ({ payload }) {
    const requests = yield call(channel);
    const responses = yield call(channel);
    let i = 0;

    for (i; i < THREADS; i += 1) {
      yield fork(handlePlanRequest, requests, responses, payload.companyIDToken);
      yield put(requests, i);
    }

    yield put(setIsLoadingPlans(true));

    while (true) {
      const response = yield take(responses);
      yield put(receivePlans(response.results));

      if (response.has_more) {
        yield put(requests, i);
        i += 1;
      } else {
        yield put(setIsLoadingPlans(false));
      }
    }
  });
}

function* handlePlanRequest(requests, responses, companyIDToken) {
  while (true) {
    const page = yield take(requests);
    const data = yield fetchPlans(page * PAGE_SIZE, PAGE_SIZE, companyIDToken).then((response) => response.json());
    yield put(responses, {
      results: data.results,
      has_more: data.has_more,
    });
  }
}

/*
  Price Request functionality.
*/
export function* updatePlansPricesLoading({ excludeIds }) {
  const allPlans = yield select(getPlans);
  const updatedPlans = setPricesLoading(allPlans, excludeIds);
  yield put(receivePlans(updatedPlans));
}
export function* requestPricingFor(employeeProfile, planIDs, companyIdToken) {
  try {
    const response = yield call(fetchPricing, employeeProfile, planIDs, companyIdToken);

    if (!response.ok && [500, 422].includes(response.status)) {
      // Log the error and exit the saga without re-triggering the action
      console.error(`Pricing request failed with status: ${response.status}`);
      return;
    }

    const data = yield response.json();
    yield put(
      receivePricing({
        planIDs: data.plan_ids,
        averageMonthlyPremiums: data.average_monthly_premiums,
        employeeCosts: data.employee_costs,
        totalCosts: data.total_costs,
      }),
    );
  } catch (error) {
    console.error("Error in requestPricingFor:", error);
  }
}
